import { useState, useEffect, useCallback } from "react";
import type {
  exchangeRateBody,
  exchangeRatePart,
} from "@/api/breakdownExchangeRateService";
import {
  useCreateBreakdownExchangeRatePart,
  useUpdateBreakdownExchangeRatePart,
} from "@/hooks/breakdownExchangeRateHooks";
import { useToast } from "@/hooks/useToast";
import {
  ConfirmAlert,
  ErrorAlert,
  WarningAlert,
} from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

// ── Constants ──────────────────────────────────────────────

export const INITIAL_EXCHANGE_RATE_BODY: exchangeRateBody = {
  part_id: 0,
  price: 0,
  currency_id: 0,
  exchange_rate_supplier_id: 0,
  currency_code: "",
  is_active: true,
  oem_other_process: [],
  spare_other_process: [],
};

// ── Validation ─────────────────────────────────────────────

export const validateExchangeRateForm = (data: exchangeRateBody) => {
  const errors: Record<string, string> = {};
  if (!data.part_id || data.part_id === 0) {
    errors.part_id = "กรุณาเลือก Part";
  }
  if (!data.price) {
    errors.price = "กรุณากรอกราคา";
  }
  if (!data.currency_id || data.currency_id === 0) {
    errors.currency_id = "กรุณาเลือกสกุลเงิน";
  }
  return errors;
};

export const validateMultipleExchangeRateForms = (
  data: exchangeRateBody[],
) => {
  const allErrors: Record<number, Record<string, string>> = {};
  data.forEach((item) => {
    const errors = validateExchangeRateForm(item);
    if (Object.keys(errors).length > 0) {
      allErrors[item.part_id] = errors;
    }
  });
  return allErrors;
};

// ── Hook ───────────────────────────────────────────────────

export function useBreakdownExchangeRateForm(supplierCode: string) {
  const { showToast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newPart, setNewPart] = useState<exchangeRateBody>({
    ...INITIAL_EXCHANGE_RATE_BODY,
  });
  const [editedPart, setEditedPart] = useState<exchangeRateBody[]>([]);
  const [selectedParts, setSelectedParts] = useState<exchangeRatePart[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<number, Record<string, string>>
  >({});

  const {
    mutate: createExchangeRatePart,
    isPending: isCreatingExchangeRatePart,
  } = useCreateBreakdownExchangeRatePart();

  const {
    mutate: updateExchangeRatePart,
    isPending: isUpdatingExchangeRatePart,
  } = useUpdateBreakdownExchangeRatePart();

  // Sync editedPart when parts are unselected during editing
  useEffect(() => {
    if (isEditing) {
      setEditedPart((prev) =>
        prev.filter((edited) =>
          selectedParts.some(
            (selected) => selected.part_id === edited.part_id,
          ),
        ),
      );
    }
  }, [selectedParts, isEditing]);

  // ── Reset ──

  const resetForm = useCallback(() => {
    setIsAdding(false);
    setIsEditing(false);
    setEditedPart([]);
    setValidationErrors({});
    setNewPart({ ...INITIAL_EXCHANGE_RATE_BODY });
  }, []);

  // ── Clear field-level errors on change ──

  const clearFieldError = useCallback(
    (partId: number, field: string) => {
      setValidationErrors((prev) => {
        if (!prev[partId]?.[field]) return prev;
        const updated = { ...prev };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: _, ...rest } = updated[partId];
        if (Object.keys(rest).length === 0) {
          delete updated[partId];
        } else {
          updated[partId] = rest;
        }
        return updated;
      });
    },
    [],
  );

  // Wrapped setNewPart that auto-clears validation errors
  const handleNewPartChange: React.Dispatch<
    React.SetStateAction<exchangeRateBody>
  > = useCallback(
    (updater) => {
      setNewPart((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        Object.keys(next).forEach((key) => {
          if (
            next[key as keyof exchangeRateBody] !==
            prev[key as keyof exchangeRateBody]
          ) {
            clearFieldError(0, key);
          }
        });
        return next;
      });
    },
    [clearFieldError],
  );

  // Wrapped setEditedPart that auto-clears validation errors
  const handleEditedPartChange: React.Dispatch<
    React.SetStateAction<exchangeRateBody[]>
  > = useCallback(
    (updater) => {
      setEditedPart((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        next.forEach((part) => {
          const original = prev.find((p) => p.part_id === part.part_id);
          if (original) {
            Object.keys(part).forEach((key) => {
              if (
                part[key as keyof exchangeRateBody] !==
                original[key as keyof exchangeRateBody]
              ) {
                clearFieldError(part.part_id, key);
              }
            });
          }
        });
        return next;
      });
    },
    [clearFieldError],
  );

  // ── Handlers ──

  const handleAddPart = useCallback(() => {
    setIsAdding(true);
    setValidationErrors({});
    setNewPart({ ...INITIAL_EXCHANGE_RATE_BODY });
  }, []);

  const handleEditPart = useCallback(() => {
    setIsEditing(true);
    setValidationErrors({});
    setEditedPart(
      selectedParts.map((part) => ({
        part_id: part.part_id,
        price: part.price,
        currency_id: part.currency_id,
        exchange_rate_supplier_id: part.exchange_rate_supplier_id,
        currency_code: part.currency_code,
        is_active: part.is_active,
        oem_other_process: part.oem_other_process,
        spare_other_process: part.spare_other_process,
      })),
    );
  }, [selectedParts]);

  const handleCancel = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleTabReset = useCallback(() => {
    setSelectedParts([]);
    resetForm();
  }, [resetForm]);

  const handleSavePart = useCallback(() => {
    const errors = validateExchangeRateForm(newPart);
    if (Object.keys(errors).length > 0) {
      setValidationErrors({ 0: errors });
      WarningAlert("Warning", "กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    createExchangeRatePart(
      { supplierCode, data: newPart },
      {
        onSuccess: () => {
          showToast("เพิ่ม Part สำเร็จ", "success");
          resetForm();
        },
        onError: (e) => ErrorAlert("Error", getAxiosErrorMessage(e)),
      },
    );
  }, [newPart, supplierCode, createExchangeRatePart, resetForm, showToast]);

  const handleSaveEditedPart = useCallback(() => {
    const errors = validateMultipleExchangeRateForms(editedPart);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      WarningAlert("Warning", "กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    updateExchangeRatePart(
      { supplierCode, data: editedPart },
      {
        onSuccess: () => {
          showToast("แก้ไข Part สำเร็จ", "success");
          resetForm();
          setSelectedParts([]);
        },
        onError: (e) => ErrorAlert("Error", getAxiosErrorMessage(e)),
      },
    );
  }, [editedPart, supplierCode, updateExchangeRatePart, resetForm, showToast]);

  const handleActivatePart = useCallback(
    async (activate: boolean) => {
      const confirm = await ConfirmAlert(
        "Confirm",
        `ต้องการ ${activate ? "Activate" : "Deactivate"} Part ที่เลือกหรือไม่?`,
        "ยืนยัน",
        "ยกเลิก",
      );
      if (!confirm) return;

      const parts: exchangeRateBody[] = selectedParts.map((part) => ({
        part_id: part.part_id,
        exchange_rate_supplier_id: part.exchange_rate_supplier_id,
        currency_id: part.currency_id,
        currency_code: part.currency_code,
        price: part.price,
        is_active: activate,
        oem_other_process: part.oem_other_process,
        spare_other_process: part.spare_other_process,
      }));

      updateExchangeRatePart(
        { supplierCode, data: parts },
        {
          onSuccess: () => {
            showToast(`${activate ? "Activate" : "Deactivate"} Part สำเร็จ`, "success");
            setSelectedParts([]);
          },
          onError: (e) => ErrorAlert("Error", getAxiosErrorMessage(e)),
        },
      );
    },
    [selectedParts, supplierCode, updateExchangeRatePart, showToast],
  );

  return {
    // State
    isAdding,
    isEditing,
    newPart,
    editedPart,
    selectedParts,
    validationErrors,
    isCreatingExchangeRatePart,
    isUpdatingExchangeRatePart,
    // Setters (with auto-clear validation)
    setNewPart: handleNewPartChange,
    setEditedPart: handleEditedPartChange,
    setSelectedParts,
    // Handlers
    handleAddPart,
    handleEditPart,
    handleCancel,
    handleSavePart,
    handleSaveEditedPart,
    handleActivatePart,
    handleTabReset,
  };
}
